import { useEffect, useMemo } from 'react'

import { useDispatch } from 'react-redux'

import { checkIfAskPhotoNeededAction } from 'actions/rating/checkIfAskPhotoNeededAction'
import { isLikeCase } from 'actions/rating/function/promo'
import {
  RatingItemType,
  RatingPromo,
} from 'components/page/PhotoRating/PhotoRating.constants'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

import { GQLRatingQuery } from '../gql/__generated__/Rating'
import { LIMIT_PHOTOS } from '../Rating.constants'

type Photos = GQLRatingQuery['photoRating']['queue']['photos']
type Photo = Photos[number]

interface PhotoItem extends Photo {
  key: string
  type: RatingItemType.photo
}

interface Promo {
  key: string
  type: RatingItemType.promo
  name: RatingPromo
}

/**
 * TODO: featuredPhotosActive
 * вынести в отдельный store для featured и оттуда доставать
 * в reducer для рейтинга не должно быть ничего что не относится к рейтингу
 */
export const useCreateQueueWithCounter = (): (PhotoItem | Promo)[] => {
  const dispatch = useDispatch()

  const {
    hasDefaultPhoto,
    incognito,
    votedCounter,
    likedCounter,
    promo,
    promoIndex,
    incognitoPositionShowed,
    currentQueuePhotos,
    rollbackCounter,
  } = useShallowEqualSelector(
    ({
      userReducer: { hasDefaultPhoto, incognito },
      photoRatingReducer: { featuredPhotosActive },
      bannerReducer: { enabled, directAdFox },
      photoRatingNext: {
        votedCounter,
        likedCounter,
        promo,
        promoIndex,
        incognitoPositionShowed,
        currentQueuePhotos,
        rollbackCounter,
      },
    }) => ({
      hasDefaultPhoto,
      incognito,
      featuredPhotosActive,
      enabled,
      directAdFox,
      votedCounter,
      likedCounter,
      promo,
      promoIndex,
      incognitoPositionShowed,
      currentQueuePhotos,
      rollbackCounter,
    })
  )

  const photosSliced = currentQueuePhotos
    .slice(0, 2)
    .map<PhotoItem>((photo) => ({
      key: photo.photoOwner.id,
      type: RatingItemType.photo,
      ...photo,
    }))

  const hasIncognitoPromo =
    hasDefaultPhoto &&
    incognito &&
    isLikeCase(likedCounter) &&
    incognitoPositionShowed !== likedCounter

  const currentPositionPromo =
    promoIndex > 0 && promoIndex - (votedCounter % LIMIT_PHOTOS)

  useEffect(() => {
    if (!hasDefaultPhoto && isLikeCase(likedCounter)) {
      dispatch(checkIfAskPhotoNeededAction())
    }
  }, [dispatch, hasDefaultPhoto, likedCounter])

  return useMemo(() => {
    const currentPromo: Promo | null = promo
      ? {
          key: `${RatingItemType.promo}-key`,
          type: RatingItemType.promo,
          name: promo,
        }
      : null

    const promoIncognito: Promo = {
      key: 'incognito-key',
      type: RatingItemType.promo,
      name: RatingPromo.incognito,
    }

    if (rollbackCounter >= 0) {
      return [...photosSliced]
    }

    switch (currentPositionPromo) {
      case 1:
        if (hasIncognitoPromo) {
          if (currentPromo) {
            return [
              promoIncognito,
              ...photosSliced.slice(0, 1),
              currentPromo,
              ...photosSliced.slice(1),
            ]
          }

          return [promoIncognito, ...photosSliced]
        }

        if (currentPromo) {
          return [
            ...photosSliced.slice(0, 1),
            currentPromo,
            ...photosSliced.slice(1),
          ]
        }

        return [...photosSliced]

      case 0:
        if (hasIncognitoPromo) {
          if (currentPromo) {
            return [promoIncognito, currentPromo, ...photosSliced]
          }

          return [promoIncognito, ...photosSliced]
        }

        if (currentPromo) {
          return [currentPromo, ...photosSliced]
        }

        return [...photosSliced]

      default:
        if (hasIncognitoPromo) {
          return [promoIncognito, ...photosSliced]
        }

        return [...photosSliced]
    }
  }, [
    currentPositionPromo,
    hasIncognitoPromo,
    photosSliced,
    promo,
    rollbackCounter,
  ])
}
